import React, { useState, useRef, useEffect, useCallback } from "react";
import { Slider, Box, Typography, IconButton } from "@mui/material";
import { EditOutlined, Pause, PlayArrow, Upload } from "@mui/icons-material";
import { toast } from "react-toastify";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import AudioPlayer from "./AudioPlayer";

const UploadAudio = ({
  setAudio,
  setAudioDuration,
  selectedAudio,
  audioRefStatic,
  IS_RAP,
  handleAudioUpload,
}) => {
  const [audioFile, setAudioFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(10);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [sliderChange, setSliderChange] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const ffmpegRef = useRef(new FFmpeg({ log: true }));
  const debounceTimeoutRef = useRef(null);

  const loadFFmpeg = async () => {
    try {
      const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm";

      // Load FFmpeg core and wasm files from the CDN
      await ffmpegRef.current.load({
        coreURL: `${baseURL}/ffmpeg-core.js`,
      });
      setLoaded(true);
    } catch (error) {
      console.error("Error loading FFmpeg:", error);
    }
  };

  useEffect(() => {
    loadFFmpeg();
  }, []);

  // Trigger trimming when FFmpeg is loaded and there's pending trim
  useEffect(() => {
    if (loaded && sliderChange && audioFile) {
      sendTrimmedAudio();
    }
  }, [loaded]);

  useEffect(() => {
    setAudioDuration(end - start);
    if (sliderChange && loaded && audioFile) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        sendTrimmedAudio().then(() => {
          isPlaying && handlePlay();
        });
      }, 300);

      return () => clearTimeout(debounceTimeoutRef.current);
    }
  }, [start, end, sliderChange, loaded, audioFile]);

  const processAudioFile = useCallback((file) => {
    if (!file) return;

    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(
        "File size exceeds 50MB limit. Please upload a smaller file."
      );
      return;
    }

    const url = URL.createObjectURL(file);
    setAudioFile(url);
    const audio = new Audio(url);

    audio.addEventListener("loadedmetadata", () => {
      const audioDur = Math.floor(audio.duration);
      setDuration(audioDur);

      setStart(0);
      // Challenge / category clips are capped at 30s server-side; default the
      // trim window to that so admins see the same length the app will store.
      const defaultEnd = IS_RAP ? audioDur : Math.min(audioDur, 30);
      setEnd(defaultEnd);
      setCurrentTime(0);
      setIsPlaying(false);

      setAudio(file);
      if (!IS_RAP && audioDur > 30) {
        toast.info("Clip longer than 30s — default trim set to first 30 seconds.");
      }
    });

    audioRef.current = audio;

    if (IS_RAP) {
      handleAudioUpload(file);
    }
  }, [IS_RAP, setAudio, handleAudioUpload]);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 1) {
      toast.error("Only one audio file can be added at a time.");
      return;
    }
    processAudioFile(e.target.files[0]);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      toast.error("Only one audio file can be added at a time.");
      return;
    }

    const file = files[0];
    if (!file.type.startsWith("audio/")) {
      toast.error("Please drop an audio file.");
      return;
    }

    processAudioFile(file);
  }, [processAudioFile]);
  const handlePlayPauseToggle = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };
  const handlePlay = () => {
    if (audioRef.current) {
      // Resume from current position if within range, otherwise start from beginning
      if (currentTime < start || currentTime >= end) {
        audioRef.current.currentTime = start;
      }
      audioRef.current.play();
      setIsPlaying(true);

      const updateProgress = () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
          if (audioRef.current.currentTime >= end) {
            handlePause();
          }
        }
      };

      audioRef.current.addEventListener("timeupdate", updateProgress);
      return () => {
        audioRef.current.removeEventListener("timeupdate", updateProgress);
      };
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Calculate progress within the trimmed range
  const calculateProgressPosition = () => {
    if (currentTime < start) return 0;
    if (currentTime > end) return 100;
    return ((currentTime - start) / (end - start)) * 100; // Percentage within the trimmed range
  };

  // Calculate width of the progress slider
  const calculateProgressWidth = () => {
    return ((end - start) / duration) * 100; // width in percentage
  };

  const sendTrimmedAudio = async () => {
    if (!loaded || !audioFile) {
      console.error("FFmpeg is not loaded or no audio selected");
      return;
    }
    try {
      const ffmpeg = ffmpegRef.current;
      const response = await fetch(audioFile);
      const arrayBuffer = await response.arrayBuffer();
      await ffmpeg.writeFile("audio.mp3", new Uint8Array(arrayBuffer));
      
      // Use -t (duration) instead of -to for more accurate trimming
      const trimDuration = end - start;
      console.log(
        "Trimming audio from",
        start,
        "for duration",
        trimDuration,
        "seconds"
      );
      
      await ffmpeg.exec([
        "-i",
        "audio.mp3",
        "-ss",
        start.toString(),
        "-t",
        trimDuration.toString(),
        "-c:a",
        "libmp3lame",
        "-q:a",
        "2",
        "output.mp3",
      ]);
      
      const data = await ffmpeg.readFile("output.mp3");
      const blob = new Blob([data.buffer], { type: "audio/mp3" });
      console.log("Trimmed audio blob size:", blob.size);
      setAudio(blob);
    } catch (error) {
      console.error("Error during trimming:", error);
    }
  };

  const handleSliderChange = (event, newValue) => {
    setSliderChange(true);
    setStart(newValue[0]);
    setEnd(newValue[1]);
  };

  const handleSeek = (event, newValue) => {
    const seekTime = start + (newValue / 100) * (end - start);
    setCurrentTime(seekTime);
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <input
          type="file"
          accept="audio/*"
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />
        {!audioFile && !selectedAudio?.uri && (
          <Box
            variant="outlined"
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: isDragging ? "2px dashed #142C5C" : "1px solid #142C5C",
              color: "#142C5C",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "12px",
              display: "flex",
              flexDirection: isDragging ? "column" : "row",
              alignItems: "center",
              justifyContent: "center",
              gap: isDragging ? "4px" : 0,
              padding: isDragging ? "16px 20px" : "5px 10px",
              cursor: "pointer",
              width: isDragging ? "280px" : "105px",
              minHeight: isDragging ? "70px" : "auto",
              background: isDragging ? "rgba(20, 44, 92, 0.06)" : "transparent",
              transition: "all 0.2s ease",
            }}
          >
            <Upload sx={{ fontSize: "20px" }} />
            {isDragging ? "Drop audio file here" : "Upload Audio"}
          </Box>
        )}
        {audioFile && (
          <Box
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              borderRadius: "8px",
              border: isDragging ? "2px dashed #142C5C" : "2px solid transparent",
              background: isDragging ? "rgba(20, 44, 92, 0.06)" : "transparent",
              transition: "all 0.2s ease",
              padding: isDragging ? "8px" : "0",
              position: "relative",
            }}
          >
            {isDragging && (
              <Typography
                sx={{
                  textAlign: "center",
                  color: "#142C5C",
                  fontWeight: 600,
                  fontSize: "11px",
                  mb: 0.5,
                }}
              >
                Drop to replace audio
              </Typography>
            )}
            <audio ref={audioRef} src={audioFile} style={{ display: "none" }} />
            {/* Trim range slider */}
            <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "center" }}>
              <Typography sx={{ fontSize: "10px", color: "#888", whiteSpace: "nowrap", minWidth: "28px" }}>
                Trim
              </Typography>
              <Slider
                value={[start, end]}
                min={0}
                max={duration}
                onChange={handleSliderChange}
                valueLabelDisplay="auto"
                sx={{
                  color: "#142C5C",
                  "& .MuiSlider-thumb": {
                    height: "14px",
                    width: "14px",
                    color: "#142C5C",
                  },
                }}
              />
              <EditOutlined
                sx={{ color: "#142C5C", cursor: "pointer", fontSize: "20px" }}
                onClick={() => fileInputRef.current.click()}
              />
            </Box>
            {/* Playhead / seek slider */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 0.5 }}>
              <Box sx={{ minWidth: "28px" }}>
                <IconButton
                  onClick={handlePlayPauseToggle}
                  sx={{
                    color: "white",
                    backgroundColor: "#142C5C",
                    padding: "5px",
                    borderRadius: "50%",
                    transition: "background-color 0.3s, opacity 0.3s",
                    "&:hover": {
                      backgroundColor: "#142C5C",
                      opacity: 0.8,
                    },
                  }}
                >
                  {isPlaying ? <Pause sx={{ fontSize: "16px" }} /> : <PlayArrow sx={{ fontSize: "16px" }} />}
                </IconButton>
              </Box>
              <Slider
                value={calculateProgressPosition()}
                min={0}
                max={100}
                onChange={handleSeek}
                sx={{
                  color: "#4fc3f7",
                  height: 6,
                  "& .MuiSlider-thumb": {
                    height: 14,
                    width: 14,
                    backgroundColor: "#fff",
                    border: "2px solid #4fc3f7",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: "0 0 0 6px rgba(79, 195, 247, 0.2)",
                    },
                    "&.Mui-active": {
                      boxShadow: "0 0 0 8px rgba(79, 195, 247, 0.3)",
                    },
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "#e0e0e0",
                    opacity: 1,
                  },
                  "& .MuiSlider-track": {
                    height: "4px",
                  },
                }}
              />
              <Typography sx={{ fontSize: "10px", color: "#666", whiteSpace: "nowrap", minWidth: "28px", textAlign: "right" }}>
                {Math.floor(currentTime >= start ? currentTime - start : 0)}s
              </Typography>
            </Box>
          </Box>
        )}
        {!audioFile && selectedAudio?.uri && (
          <Box
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderRadius: "8px",
              border: isDragging ? "2px dashed #142C5C" : "2px solid transparent",
              background: isDragging ? "rgba(20, 44, 92, 0.06)" : "transparent",
              padding: isDragging ? "8px" : "0",
              transition: "all 0.2s ease",
            }}
          >
            {isDragging ? (
              <Typography
                sx={{
                  color: "#142C5C",
                  fontWeight: 600,
                  fontSize: "12px",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                Drop to replace audio
              </Typography>
            ) : (
              <>
                <AudioPlayer audioRef={audioRefStatic} />
                <EditOutlined
                  sx={{ color: "#142C5C", cursor: "pointer" }}
                  onClick={() => fileInputRef.current.click()}
                />
              </>
            )}
          </Box>
        )}
      </Box>
      {audioFile && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "-8px",
              px: 1,
            }}
          >
            <Typography variant="body1" sx={{ fontSize: "12px" }}>
              {" "}
              Start : <span style={{ fontWeight: 400 }}>{start}s</span>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "12px" }}>
              Duration : <span style={{ fontWeight: 400 }}>{end - start}s</span>
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "12px" }}>
              {" "}
              End : <span style={{ fontWeight: 400 }}>{end}s</span>
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

export default UploadAudio;
